/**
 * Session expiry handling
 *
 * Logs the user out when the server says their session is no longer valid, for
 * both axios and fetch requests.
 *
 * Only the auth middleware's 401 bodies count (appprotect / appmasterprotect).
 * Other 401s from route handlers ("Incorrect password", an expired Microsoft
 * token, ...) are ordinary errors and must not log anyone out. Server-unreachable
 * errors (proxy errors, 502/503/504) never log out either.
 */
import axios from "axios";
import { logout } from "../features/auth/authSlice";
import { masterLogout } from "../features/masterAuth/masterAuthSlice";

const SESSION_FAILURE_STATUSES = ["Not authorized", "Not authorized, no token", "deleted"];
export const SESSION_EXPIRED_FLAG = "sessionExpired";

let loggingOut = false;

const readStoredToken = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key))?.token || null;
  } catch (e) {
    return null;
  }
};

const bearerToken = (authorization) =>
  typeof authorization === "string" && authorization.startsWith("Bearer ")
    ? authorization.slice("Bearer ".length).trim()
    : null;

// Which stored session (if any) a failed request belongs to. A request sent with
// no token, or with an old token after the user signed in again, says nothing
// about the current session, so it is ignored.
const sessionForToken = (requestToken) => {
  if (!requestToken) return null;
  if (requestToken === readStoredToken("user")) return "user";
  if (requestToken === readStoredToken("masterUser")) return "master";
  return null;
};

const endSession = async (store, queryClient, session) => {
  loggingOut = true;
  try {
    // Both thunks clear localStorage (the user one keeps the 2FA keys)
    await store.dispatch(session === "master" ? masterLogout() : logout());
    queryClient.clear();
    sessionStorage.setItem(SESSION_EXPIRED_FLAG, "1");
  } catch (e) {
    console.error("[session-expiry] logout failed:", e);
  }
  // Full reload so no stale state (redux, query cache, sockets, timers) survives
  window.location.replace(session === "master" ? "/master-user/login" : "/");
};

// Requests that fail while the page is being torn down never settle, so the
// calling components don't flash "Something went wrong" toasts before the reload.
const pendingForever = () => new Promise(() => {});

export function setupSessionExpiryHandling({ store, queryClient }) {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (axios.isCancel(error) || error.response?.status !== 401) {
        return Promise.reject(error);
      }
      if (!SESSION_FAILURE_STATUSES.includes(error.response?.data?.status)) {
        return Promise.reject(error);
      }
      const headers = error.config?.headers;
      const authorization = headers?.get?.("Authorization") ?? headers?.Authorization ?? headers?.authorization;
      const session = sessionForToken(bearerToken(authorization));
      if (!session) {
        return Promise.reject(error);
      }
      if (!loggingOut) {
        endSession(store, queryClient, session);
      }
      return pendingForever();
    }
  );

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    if (response.status !== 401) {
      return response;
    }

    let authorization = null;
    try {
      const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
      authorization = headers.get("Authorization");
    } catch (e) {
      return response;
    }
    const session = sessionForToken(bearerToken(authorization));
    if (!session) {
      return response;
    }

    let body = null;
    try {
      body = await response.clone().json();
    } catch (e) {
      return response;
    }
    if (!SESSION_FAILURE_STATUSES.includes(body?.status)) {
      return response;
    }
    if (!loggingOut) {
      endSession(store, queryClient, session);
    }
    return pendingForever();
  };
}
